import React, { createContext, useContext, useMemo } from 'react';

type TabContextValue = {
	id: string;
	isOverflowing: boolean;
	isSelected: boolean;
};

const TabContext = createContext<TabContextValue | null>(null);

export type TabProviderProps = TabContextValue & {
	children: React.ReactNode;
};

export const TabProvider = ({ children, id, isOverflowing, isSelected }: TabProviderProps) => {
	const value = useMemo(
		() => ({
			id,
			isOverflowing,
			isSelected,
		}),
		[id, isOverflowing, isSelected],
	);

	return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

const useTab = () => {
	const context = useContext(TabContext);
	if (!context) {
		throw new Error('useTab must be used within a TabProvider');
	}
	return context;
};

export const useTabId = () => {
	const { id } = useTab();
	return id;
};

export const useIsTabOverflowing = () => {
	const { isOverflowing } = useTab();
	return isOverflowing;
};

export const useIsTabSelected = () => {
	const { isSelected } = useTab();
	return isSelected;
};
